import React, { useState } from "react";
import { ChevronRight } from "lucide-react";

export default function SentimentTree({ tree, onSelect }) {
  return (
    <div className="h-full overflow-auto thin-scroll" data-testid="sentiment-tree">
      <div className="divide-y divide-slate-100">
        {tree.map((v) => <VentureNode key={v.venture} node={v} onSelect={onSelect} />)}
      </div>
    </div>
  );
}

function RiskChip({ value }) {
  const sev = value >= 70 ? "critical" : value >= 55 ? "high" : value >= 40 ? "medium" : "low";
  const colors = { critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#059669" };
  return (
    <span className="font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-sm"
      style={{ color: colors[sev], background: `${colors[sev]}14` }}>
      R{value.toFixed(0)}
    </span>
  );
}

function Chev({ open }) {
  return <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />;
}

function VentureNode({ node, onSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <div className="flex items-stretch group">
        <button
          className="tree-row flex items-center gap-2 px-2 py-2.5"
          onClick={() => setOpen(!open)}
          aria-label={`toggle ${node.venture}`}
        >
          <Chev open={open} />
        </button>
        <button
          className="tree-row flex-1 flex items-center gap-2 pr-4 py-2.5 text-left"
          onClick={() => onSelect && onSelect({ venture: node.venture })}
          data-testid={`tree-venture-${node.venture.replace(/\s+/g, "-")}`}
        >
          <span className="font-display font-semibold text-sm flex-1">{node.venture}</span>
          <span className="text-[10px] font-mono text-slate-500">{node.count} signals</span>
          <RiskChip value={node.avg_risk} />
        </button>
      </div>
      {open && (
        <div className="bg-slate-50/40">
          {node.sites.map((s) => <SiteNode key={s.site} node={s} venture={node.venture} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
}

function SiteNode({ node, venture, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pl-6 border-l border-slate-200 ml-4">
      <div className="flex items-stretch">
        <button className="tree-row flex items-center gap-2 px-2 py-2" onClick={() => setOpen(!open)} aria-label={`toggle ${node.site}`}>
          <Chev open={open} />
        </button>
        <button
          className="tree-row flex-1 flex items-center gap-2 pr-3 py-2 text-left"
          onClick={() => onSelect && onSelect({ venture, site: node.site })}
          data-testid={`tree-site-${node.site.replace(/\s+/g, "-")}`}
        >
          <span className="text-sm flex-1">{node.site}</span>
          <span className="text-[10px] font-mono text-slate-500">{node.count}</span>
          <RiskChip value={node.avg_risk} />
        </button>
      </div>
      {open && (
        <div>
          {node.categories.map((c) => <CatNode key={c.category} node={c} venture={venture} site={node.site} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
}

function CatNode({ node, venture, site, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pl-5 border-l border-slate-200 ml-4">
      <div className="flex items-stretch">
        <button className="tree-row flex items-center gap-2 px-2 py-1.5" onClick={() => setOpen(!open)} aria-label={`toggle ${node.category}`}>
          <Chev open={open} />
        </button>
        <button
          className="tree-row flex-1 flex items-center gap-2 pr-3 py-1.5 text-left"
          onClick={() => onSelect && onSelect({ venture, site, category: node.category })}
          data-testid={`tree-cat-${node.category.slice(0, 16).replace(/[^a-zA-Z]/g, "")}`}
        >
          <span className="text-[13px] text-slate-700 flex-1">{node.category}</span>
          <span className="text-[10px] font-mono text-slate-500">{node.count}</span>
          <RiskChip value={node.avg_risk} />
        </button>
      </div>
      {open && (
        <div className="pl-5 border-l border-slate-200 ml-3 py-1">
          {node.subpoints.map((sp) => (
            <button
              key={sp.name}
              className="tree-row w-full flex items-center gap-2 px-3 py-1.5 text-left"
              onClick={() => onSelect && onSelect({ venture, site, category: node.category, sub: sp.name })}
              data-testid={`tree-sub-${sp.name.replace(/\s+/g, "-")}`}
            >
              <span className="h-1 w-1 rounded-full bg-slate-400" />
              <span className="text-xs text-slate-600 flex-1">{sp.name}</span>
              <span className="text-[10px] font-mono text-slate-400">{sp.count}</span>
              <RiskChip value={sp.avg_risk} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
